import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getPublicSiteNotice,
  type PublicSiteNotice,
} from "../services/adminApi";

const HIDDEN_NOTICE_PAGES = ["/login", "/register"];

const styleMap: Record<
  PublicSiteNotice["variant"],
  {
    wrapper: string;
    link: string;
  }
> = {
  info: {
    wrapper: "bg-blue-50 text-blue-900 border-blue-200",
    link: "text-blue-800 hover:text-blue-950",
  },
  success: {
    wrapper: "bg-emerald-50 text-emerald-900 border-emerald-200",
    link: "text-emerald-800 hover:text-emerald-950",
  },
  warning: {
    wrapper: "bg-amber-50 text-amber-900 border-amber-200",
    link: "text-amber-800 hover:text-amber-950",
  },
  danger: {
    wrapper: "bg-red-50 text-red-900 border-red-200",
    link: "text-red-800 hover:text-red-950",
  },
};

export default function SiteNoticeBar() {
  const location = useLocation();
  const [notice, setNotice] = useState<PublicSiteNotice | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadNotice = async () => {
      try {
        const result = await getPublicSiteNotice();
        if (isMounted) {
          setNotice(result);
        }
      } catch {
        if (isMounted) {
          setNotice(null);
        }
      }
    };

    void loadNotice();

    return () => {
      isMounted = false;
    };
  }, []);

  const isHidden = HIDDEN_NOTICE_PAGES.includes(location.pathname);

  const visibleNotice = useMemo(() => {
    if (!notice || !notice.active || !notice.message.trim()) {
      return null;
    }
    return notice;
  }, [notice]);

  if (isHidden || !visibleNotice) {
    return null;
  }

  const styles = styleMap[visibleNotice.variant];

  return (
    <div className={`w-full border-b px-3 py-2 text-sm ${styles.wrapper}`}>
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center sm:px-6 lg:px-8">
        <span className="font-medium">{visibleNotice.message}</span>
        {visibleNotice.linkUrl && visibleNotice.linkLabel && (
          <a
            href={visibleNotice.linkUrl}
            target="_blank"
            rel="noreferrer"
            className={`font-semibold underline underline-offset-2 transition ${styles.link}`}
          >
            {visibleNotice.linkLabel}
          </a>
        )}
      </div>
    </div>
  );
}
